import { IconWand } from '@tabler/icons-react';
import { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import GenerateContentPopup from './GenerateContentPopup';

const GenerateContent = ({
  quillEditorRef,
  setIsLoading,
  generateContentRef,
  aiModel,
  provider,
  userError,
  setUserError,
  controllerRef,
  setShowAbortButton,
  handleBrandContextChange,
}) => {
  const [lastHoveredElementIndex, setLastHoveredElementIndex] = useState();
  const [generatePopupStyles, setGeneratePopupStyles] = useState();

  const hideGeneratePopup = useCallback(() => {
    if (!generatePopupStyles) {
      return;
    }

    const qlEditor = document.querySelector('.ql-editor');
    const elementClicked = qlEditor.children[lastHoveredElementIndex];
    elementClicked.style.removeProperty('padding-bottom');

    setGeneratePopupStyles(null);
  }, [generatePopupStyles, lastHoveredElementIndex]);

  useImperativeHandle(generateContentRef, () => ({
    async hideGenerateContent() {
      hideGeneratePopup();
    },
  }));

  useEffect(() => {
    const qlEditor = document.querySelector('.ql-editor');
    const iconShowGenerateContentPopup = document.querySelector('.icon-show-generate-content-popup');

    if (!qlEditor || !iconShowGenerateContentPopup) {
      return;
    }

    const debounce = (func, timeout) => {
      let timer;

      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          func.apply(this, args);
        }, timeout);
      };
    };

    const handleMouseOver = (e) => {
      // If generate popup is visible do not show the button
      if (generatePopupStyles) {
        return;
      }

      let element = e.target;
      if (element.tagName === 'IMG') {
        element = element.closest('p');
      }

      // We listen only for the child elements of the div with class "ql-editor" and images
      if (!element.parentElement.classList.contains('ql-editor')) {
        return;
      }

      const elementPosition = element.getBoundingClientRect();

      const contextConfiguration = document.getElementById('context-configuration');
      const contextConfigurationHeight = contextConfiguration.offsetHeight;
      const headerHeight = 128;
      const paddingLeftAndRight = 120;
      const topPixels =
        elementPosition.bottom + document.documentElement.scrollTop - headerHeight - contextConfigurationHeight;
      const maxWidthPixels = elementPosition.right - elementPosition.left - paddingLeftAndRight;

      iconShowGenerateContentPopup.style.top = `${topPixels}px`;
      iconShowGenerateContentPopup.style['max-width'] = `${maxWidthPixels}px`;
      iconShowGenerateContentPopup.classList.add('icon-show-generate-content-popup--visible');

      const children = [...element.parentNode.childNodes];
      const elementIndex = children.indexOf(element);
      setLastHoveredElementIndex(elementIndex);
    };

    const handleMouseLeave = () => {
      iconShowGenerateContentPopup.classList.remove('icon-show-generate-content-popup--visible');
    };

    const handleMouseOverWithDebounce = debounce(handleMouseOver, 10);
    const handleMouseLeaveWithDebounce = debounce(handleMouseLeave, 10);

    qlEditor.addEventListener('mouseover', handleMouseOverWithDebounce);
    qlEditor.addEventListener('mouseleave', handleMouseLeaveWithDebounce);

    return () => {
      qlEditor.removeEventListener('mouseover', handleMouseOverWithDebounce);
      qlEditor.removeEventListener('mouseleave', handleMouseLeaveWithDebounce);
    };
  }, [generatePopupStyles]);

  const showGenerateContent = async () => {
    const mdxEditor = document.querySelector('.ql-editor');
    const hoveredElement = mdxEditor.children[lastHoveredElementIndex];
    hoveredElement.style['padding-bottom'] = '60px';
    const hoveredElementPosition = hoveredElement.getBoundingClientRect();

    const contextConfiguration = document.getElementById('context-configuration');
    const contextConfigurationHeight = contextConfiguration.offsetHeight;

    setGeneratePopupStyles({
      top: `${hoveredElementPosition.bottom + document.documentElement.scrollTop - 142 - contextConfigurationHeight}px`,
      width: `${hoveredElementPosition.right - hoveredElementPosition.left - 120}px`,
      display: 'flex',
    });
  };

  return (
    <>
      {!generatePopupStyles && (
        <div className="icon-show-generate-content-popup">
          <span onClick={showGenerateContent}>
            <IconWand size={14} />
          </span>
        </div>
      )}

      {generatePopupStyles && (
        <GenerateContentPopup
          generatePopupStyles={generatePopupStyles}
          hideGeneratePopup={hideGeneratePopup}
          setIsLoading={setIsLoading}
          quillEditorRef={quillEditorRef}
          lastHoveredElementIndex={lastHoveredElementIndex}
          aiModel={aiModel}
          provider={provider}
          setUserError={setUserError}
          controllerRef={controllerRef}
          setShowAbortButton={setShowAbortButton}
          handleBrandContextChange={handleBrandContextChange}
        />
      )}
    </>
  );
};

export default GenerateContent;
